// libraries.
import { useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";

// interfaces.
import { IncentiveStep } from "../../rulesData";
import {
  RawRule,
  RawRuleItem,
  RawCondition,
  RawConditionComplex,
  RawConditionOperator,
} from "@store/apiSlices/incentiveRules/interfaces/rawRules.interfaces";

// store.
import { useRulesSlice } from "@store/apiSlices/incentiveRules/slices/rules";

// ─── Form values ──────────────────────────────────────────────────────────────

export interface SubConditionValues {
  left: string;
  type: RawConditionOperator;
  right: string;
}

export interface EditStepFormValues {
  name: string;
  description: string;
  priority: number;
  isActive: boolean;
  // Day range (only present when rule has dias_full conditions)
  dayRangeFrom?: number;
  dayRangeTo?: number | "";
  // Percentage (only present when rule has descuentos/api_bonos actions)
  percentage?: number;
  // Condition
  conditionMode: "simple" | "and" | "or";
  conditionLeft: string;
  conditionType: RawConditionOperator;
  conditionRight: string;
  subConditions: SubConditionValues[];
}

// ─── Helpers — read ───────────────────────────────────────────────────────────

const isComplexCondition = (
  condition: RawRule["condition"],
): condition is RawConditionComplex => "conditions" in condition;

const extractDayRange = (
  rawRule: RawRuleItem,
): { from: number; to: number | null } | null => {
  const { condition } = rawRule.rule;
  if (!isComplexCondition(condition)) return null;

  const diasConditions = condition.conditions.filter(
    (c) => typeof c.left === "string" && c.left.includes("dias_full"),
  );
  if (diasConditions.length === 0) return null;

  const fromCond = diasConditions.find((c) => c.type === "greater_or_equal");
  const toCond = diasConditions.find((c) => c.type === "less_than");

  const from = fromCond ? (fromCond.right as number) : 0;
  const to = toCond ? (toCond.right as number) - 1 : null;

  return { from, to };
};

const extractPercentage = (rawRule: RawRuleItem): number | null => {
  const action = rawRule.rule.actions.find(
    (a) =>
      (a.tool === "descuentos" || a.tool === "api_bonos") &&
      a.params.percentage !== undefined,
  );
  if (!action) return null;
  return (action.params.percentage as number) * 100;
};

/** Initializes form condition fields from a raw condition. */
const initConditionFields = (
  condition: RawCondition,
): Pick<EditStepFormValues, "conditionMode" | "conditionLeft" | "conditionType" | "conditionRight" | "subConditions"> => {
  if (isComplexCondition(condition)) {
    return {
      conditionMode: condition.type,
      conditionLeft: "",
      conditionType: "equals",
      conditionRight: "",
      subConditions: condition.conditions.map((c) => ({
        left: c.left,
        type: c.type,
        right: String(c.right),
      })),
    };
  }
  return {
    conditionMode: "simple",
    conditionLeft: condition.left,
    conditionType: condition.type,
    conditionRight: String(condition.right),
    subConditions: [],
  };
};

// ─── Helpers — write ──────────────────────────────────────────────────────────

/** Coerces a string value to number if it looks like one. */
const parseRight = (v: string): string | number => {
  const n = Number(v);
  return !isNaN(n) && v.trim() !== "" ? n : v;
};

/** Builds a RawCondition from the form condition fields. */
const buildCondition = (data: EditStepFormValues): RawCondition => {
  if (data.conditionMode === "simple") {
    return {
      left: data.conditionLeft,
      type: data.conditionType,
      right: parseRight(data.conditionRight),
    };
  }
  return {
    type: data.conditionMode,
    conditions: data.subConditions.map((c) => ({
      left: c.left,
      type: c.type,
      right: parseRight(c.right),
    })),
  };
};

const injectDayRange = (
  rule: RawRule,
  from: number,
  toInclusive: number | null,
): RawRule => {
  if (!isComplexCondition(rule.condition)) return rule;

  const updatedConditions = rule.condition.conditions
    .filter((c) => {
      if (
        typeof c.left === "string" &&
        c.left.includes("dias_full") &&
        c.type === "less_than" &&
        toInclusive === null
      )
        return false;
      return true;
    })
    .map((c) => {
      if (typeof c.left !== "string" || !c.left.includes("dias_full"))
        return c;
      if (c.type === "greater_or_equal") return { ...c, right: from };
      if (c.type === "less_than" && toInclusive !== null)
        return { ...c, right: toInclusive + 1 };
      return c;
    });

  return {
    ...rule,
    condition: { ...rule.condition, conditions: updatedConditions },
  };
};

const injectPercentage = (rule: RawRule, percentageDisplay: number): RawRule => {
  const pct = percentageDisplay / 100;
  return {
    ...rule,
    actions: rule.actions.map((a) => {
      if (
        (a.tool === "descuentos" || a.tool === "api_bonos") &&
        a.params.percentage !== undefined
      )
        return { ...a, params: { ...a.params, percentage: pct } };
      return a;
    }),
  };
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const emptySubCondition: SubConditionValues = {
  left: "",
  type: "equals",
  right: "",
};

interface UseEditStepFormProps {
  step: IncentiveStep | null;
  rawRule: RawRuleItem | null;
  country: string;
  onClose: () => void;
}

export const useEditStepForm = ({
  step,
  rawRule,
  country,
  onClose,
}: UseEditStepFormProps) => {
  const { saveIncentiveStep, isSaving } = useRulesSlice();
  const methods = useForm<EditStepFormValues>({
    defaultValues: {
      conditionMode: "simple",
      conditionLeft: "",
      conditionType: "equals",
      conditionRight: "",
      subConditions: [],
    },
  });
  const { reset, handleSubmit, control } = methods;

  const subConditionArray = useFieldArray({ control, name: "subConditions" });

  const dayRange = rawRule ? extractDayRange(rawRule) : null;
  const percentageInitial = rawRule ? extractPercentage(rawRule) : null;
  const hasDayRange = dayRange !== null;
  const hasPercentage = percentageInitial !== null;

  useEffect(() => {
    if (step && rawRule) {
      const dr = extractDayRange(rawRule);
      const pct = extractPercentage(rawRule);
      reset({
        name: rawRule.rule.name,
        description: rawRule.rule.description,
        priority: rawRule.rule.priority,
        isActive: rawRule.is_active === "active",
        ...(dr !== null && { dayRangeFrom: dr.from, dayRangeTo: dr.to ?? "" }),
        ...(pct !== null && { percentage: pct }),
        ...initConditionFields(rawRule.rule.condition),
      });
    }
  }, [step, rawRule, reset]);

  const onSubmit = handleSubmit(async (data: EditStepFormValues) => {
    if (!step || !rawRule) return;

    let updatedRule: RawRule = {
      ...rawRule.rule,
      id: rawRule.rule_id,
      name: data.name,
      description: data.description,
      priority: data.priority,
      condition: buildCondition(data),
    };

    if (hasDayRange && data.dayRangeFrom !== undefined) {
      const toInclusive =
        data.dayRangeTo === "" || data.dayRangeTo === undefined
          ? null
          : Number(data.dayRangeTo);
      updatedRule = injectDayRange(updatedRule, data.dayRangeFrom, toInclusive);
    }

    if (hasPercentage && data.percentage !== undefined) {
      updatedRule = injectPercentage(updatedRule, data.percentage);
    }

    const updatedStep: IncentiveStep = {
      ...step,
      name: data.name,
      description: data.description,
    };

    await saveIncentiveStep(country, updatedStep, updatedRule, rawRule.rule_id);
    onClose();
  });

  return {
    methods,
    onSubmit,
    isSaving,
    hasDayRange,
    hasPercentage,
    subConditionArray,
  };
};
